import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CreateCullRequest, Cull } from '../types';
import { cullsApi } from '../services/api';
import { useAuth } from '../context/AuthContext';
import './CullForm.css';

const SPECIES_OPTIONS = [
  'Red Deer',
  'Roe Deer',
  'Fallow Deer',
  'Muntjac',
  'Sika Deer',
  'Chinese Water Deer',
  'Fox',
  'Rabbit',
];

const SEX_OPTIONS = ['Male', 'Female', 'Unknown'];

const AGE_OPTIONS = ['Young', 'Young Adult', 'Adult', 'Mature Adult', 'Old'];

const CONDITION_OPTIONS = ['Excellent', 'Good', 'Fair', 'Poor', 'Very Poor'];

const SHOT_PLACEMENT_OPTIONS = [
  'Head/Neck',
  'Heart/Lung',
  'Shoulder',
  'Liver',
  'Gut Shot',
  'Limb',
];

const ANTLER_CONDITION_OPTIONS = ['Excellent', 'Good', 'Fair', 'Poor', 'Damaged'];

const CullForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isEditing = !!id;

  const [formData, setFormData] = useState<CreateCullRequest>({
    date: new Date().toISOString().split('T')[0],
    time: '06:00',
    location_name: '',
    grid_ref: '',
    latitude: undefined,
    longitude: undefined,
    species: '',
    sex: '',
    age_estimate: '',
    weight: undefined,
    condition: '',
    condition_notes: '',
    antlers_present: false,
    antler_points: undefined,
    antler_condition: '',
    shot_placement: '',
    shot_distance: '',
    notes: '',
  });

  const [customSpecies, setCustomSpecies] = useState('');
  const [customShotPlacement, setCustomShotPlacement] = useState('');
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEditing && id) {
      const fetchCull = async () => {
        try {
          const cull = await cullsApi.getCull(id);
          setFormData({
            date: cull.date,
            time: cull.time,
            location_name: cull.location_name,
            grid_ref: cull.grid_ref || '',
            latitude: cull.latitude,
            longitude: cull.longitude,
            species: cull.species,
            sex: cull.sex,
            age_estimate: cull.age_estimate,
            weight: cull.weight,
            condition: cull.condition,
            condition_notes: cull.condition_notes || '',
            shot_placement: cull.shot_placement,
            shot_distance: cull.shot_distance,
            antlers_present: cull.antlers_present || false,
            antler_points: cull.antler_points,
            antler_condition: cull.antler_condition || '',
            notes: cull.notes || '',
          });
        } catch (error) {
          console.error('Error fetching cull:', error);
          setErrors({ submit: 'Failed to load cull data.' });
        }
      };
      fetchCull();
    }
  }, [isEditing, id]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked,
      }));
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: value ? parseFloat(value) : undefined,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value,
      }));
    }

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: '',
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.date) newErrors.date = 'Date is required';
    if (!formData.time) newErrors.time = 'Time is required';
    if (!formData.location_name.trim()) newErrors.location_name = 'Location is required';
    if (!formData.species) newErrors.species = 'Species is required';
    if (!formData.sex) newErrors.sex = 'Sex is required';
    if (!formData.age_estimate) newErrors.age_estimate = 'Age estimate is required';
    if (!formData.condition) newErrors.condition = 'Condition is required';
    if (!formData.shot_placement) newErrors.shot_placement = 'Shot placement is required';

    // Antler points are now optional even when antlers are present

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    
    try {
      const submitData = {
        ...formData,
        species: formData.species === 'Other' ? customSpecies : formData.species,
        shot_placement: formData.shot_placement === 'Other' ? customShotPlacement : formData.shot_placement,
      };

      if (isEditing) {
        await cullsApi.updateCull(id!, submitData);
      } else {
        if (!user?.uid) {
          throw new Error('User not authenticated');
        }
        await cullsApi.createCull(submitData, user.uid);
      }
      
      navigate('/data');
    } catch (error) {
      console.error('Error submitting cull:', error);
      setErrors({ submit: 'Failed to submit cull record. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="cull-form-page">
      <div className="form-header">
        <h1>{isEditing ? 'Edit Cull Record' : 'Add New Cull Record'}</h1>
      </div>

      <form onSubmit={handleSubmit} className="cull-form">
        {errors.submit && (
          <div className="error-message global-error">{errors.submit}</div>
        )}

        <div className="form-section">
          <h3>Date & Time</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="date">Date of Shot *</label>
              <input
                type="date"
                id="date"
                name="date"
                value={formData.date}
                onChange={handleInputChange}
                className={errors.date ? 'error' : ''}
                required
              />
              {errors.date && <span className="error-message">{errors.date}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="time">Time of Shot *</label>
              <input
                type="time"
                id="time"
                name="time"
                value={formData.time}
                onChange={handleInputChange}
                className={errors.time ? 'error' : ''}
                required
              />
              {errors.time && <span className="error-message">{errors.time}</span>}
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Location</h3>
          <div className="form-group">
            <label htmlFor="location_name">Location Name *</label>
            <input
              type="text"
              id="location_name"
              name="location_name"
              value={formData.location_name}
              onChange={handleInputChange}
              className={errors.location_name ? 'error' : ''}
              placeholder="e.g., Yorkshire Dales, Wensleydale"
              required
            />
            {errors.location_name && <span className="error-message">{errors.location_name}</span>}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="grid_ref">Grid Reference (optional)</label>
              <input
                type="text"
                id="grid_ref"
                name="grid_ref"
                value={formData.grid_ref}
                onChange={handleInputChange}
                placeholder="e.g., SD9185"
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="latitude">Latitude (optional)</label>
              <input
                type="number"
                id="latitude"
                name="latitude"
                value={formData.latitude || ''}
                onChange={handleInputChange}
                step="0.000001"
                placeholder="e.g., 54.3781"
              />
            </div>
            <div className="form-group">
              <label htmlFor="longitude">Longitude (optional)</label>
              <input
                type="number"
                id="longitude"
                name="longitude"
                value={formData.longitude || ''}
                onChange={handleInputChange}
                step="0.000001"
                placeholder="e.g., -2.0441"
              />
            </div>
          </div>
        </div>

        <div className="form-section">
          <h3>Animal Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="species">Species *</label>
              <select
                id="species"
                name="species"
                value={formData.species}
                onChange={handleInputChange}
                className={errors.species ? 'error' : ''}
                required
              >
                <option value="">Select species</option>
                {SPECIES_OPTIONS.map(species => (
                  <option key={species} value={species}>{species}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              {errors.species && <span className="error-message">{errors.species}</span>}
            </div>
            {formData.species === 'Other' && (
              <div className="form-group">
                <label htmlFor="customSpecies">Specify Species</label>
                <input
                  type="text"
                  id="customSpecies"
                  value={customSpecies}
                  onChange={(e) => setCustomSpecies(e.target.value)}
                  placeholder="Enter species name"
                  required
                />
              </div>
            )}
          </div>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="sex">Sex *</label>
              <select
                id="sex"
                name="sex"
                value={formData.sex}
                onChange={handleInputChange}
                className={errors.sex ? 'error' : ''}
                required
              >
                <option value="">Select sex</option>
                {SEX_OPTIONS.map(sex => (
                  <option key={sex} value={sex}>{sex}</option>
                ))}
              </select>
              {errors.sex && <span className="error-message">{errors.sex}</span>}
            </div>
            <div className="form-group">
              <label htmlFor="age_estimate">Age Estimate *</label>
              <select
                id="age_estimate"
                name="age_estimate"
                value={formData.age_estimate}
                onChange={handleInputChange}
                className={errors.age_estimate ? 'error' : ''}
                required
              >
                <option value="">Select age</option>
                {AGE_OPTIONS.map(age => (
                  <option key={age} value={age}>{age}</option>
                ))}
              </select>
              {errors.age_estimate && <span className="error-message">{errors.age_estimate}</span>}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="weight">Weight (kg) (optional)</label>
            <input
              type="number"
              id="weight"
              name="weight"
              value={formData.weight || ''}
              onChange={handleInputChange}
              step="0.1"
              placeholder="e.g., 120.5"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Condition</h3>
          <div className="form-group">
            <label htmlFor="condition">Condition *</label>
            <select
              id="condition"
              name="condition"
              value={formData.condition}
              onChange={handleInputChange}
              className={errors.condition ? 'error' : ''}
              required
            >
              <option value="">Select condition</option>
              {CONDITION_OPTIONS.map(condition => (
                <option key={condition} value={condition}>{condition}</option>
              ))}
            </select>
            {errors.condition && <span className="error-message">{errors.condition}</span>}
          </div>
          <div className="form-group">
            <label htmlFor="condition_notes">Condition Notes</label>
            <textarea
              id="condition_notes"
              name="condition_notes"
              value={formData.condition_notes}
              onChange={handleInputChange}
              rows={3}
              placeholder="Additional notes about the animal's condition"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Antlers</h3>
          <div className="form-group checkbox-group">
            <label htmlFor="antlers_present">
              <input
                type="checkbox"
                id="antlers_present"
                name="antlers_present"
                checked={formData.antlers_present}
                onChange={handleInputChange}
              />
              Antlers Present
            </label>
          </div>
          {formData.antlers_present && (
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="antler_points">Number of Points (optional)</label>
                <input
                  type="number"
                  id="antler_points"
                  name="antler_points"
                  value={formData.antler_points || ''}
                  onChange={handleInputChange}
                  min="0"
                  className={errors.antler_points ? 'error' : ''}
                />
                {errors.antler_points && <span className="error-message">{errors.antler_points}</span>}
              </div>
              <div className="form-group">
                <label htmlFor="antler_condition">Antler Condition</label>
                <select
                  id="antler_condition"
                  name="antler_condition"
                  value={formData.antler_condition}
                  onChange={handleInputChange}
                >
                  <option value="">Select condition</option>
                  {ANTLER_CONDITION_OPTIONS.map(condition => (
                    <option key={condition} value={condition}>{condition}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
        </div>

        <div className="form-section">
          <h3>Shot Details</h3>
          <div className="form-row">
            <div className="form-group">
              <label htmlFor="shot_placement">Shot Placement *</label>
              <select
                id="shot_placement"
                name="shot_placement"
                value={formData.shot_placement}
                onChange={handleInputChange}
                className={errors.shot_placement ? 'error' : ''}
                required
              >
                <option value="">Select placement</option>
                {SHOT_PLACEMENT_OPTIONS.map(placement => (
                  <option key={placement} value={placement}>{placement}</option>
                ))}
                <option value="Other">Other</option>
              </select>
              {errors.shot_placement && <span className="error-message">{errors.shot_placement}</span>}
            </div>
            {formData.shot_placement === 'Other' && (
              <div className="form-group">
                <label htmlFor="customShotPlacement">Specify Shot Placement</label>
                <input
                  type="text"
                  id="customShotPlacement"
                  value={customShotPlacement}
                  onChange={(e) => setCustomShotPlacement(e.target.value)}
                  placeholder="Enter shot placement"
                  required
                />
              </div>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="shot_distance">Distance of Shot</label>
            <input
              type="text"
              id="shot_distance"
              name="shot_distance"
              value={formData.shot_distance}
              onChange={handleInputChange}
              placeholder="e.g., 150 meters, 80 yards"
            />
          </div>
        </div>

        <div className="form-section">
          <h3>Additional Notes</h3>
          <div className="form-group">
            <label htmlFor="notes">Notes</label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              rows={4}
              placeholder="Any additional notes about the cull"
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={() => navigate('/data')}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (isEditing ? 'Update Cull' : 'Add Cull')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CullForm;